import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { isNil, uniq } from 'lodash'
import { faker } from '@faker-js/faker'

import { createQAUser, CREATE_QA_USER_KEY } from '../qaUsers.slice'
import { Button, Form, Dialog, Alert, Text, Card } from '@/ui'
import { Pathway } from '../qaFeatures.slice'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import {
  FILING_STATUSES,
  FilingStatusOptions,
  TaxEntityTypeOptions,
  YesNoOptions,
} from '@/features/taxes/taxConstants'
import { useAppDispatch } from '@/utils/typeHelpers'
import { StripeProduct } from '@/actions/settings/billingActions'
import {
  getFieldNames,
  makeReqBoolSchema,
  makeReqEmailSchema,
  makeReqStringSchema,
  valsToDropdownOptions,
} from '@/base'
import { PracticeType } from '@/constants'

const NewUserModal = ({
  open,
  onClose,
  pathwayIdent,
  pathways,
  stripeProducts,
}: {
  open: boolean
  onClose: () => void
  pathwayIdent: string
  pathways: Pathway[]
  stripeProducts?: StripeProduct[]
}) => {
  const dispatch = useAppDispatch()
  const loading = useReselector(getIsFetching, CREATE_QA_USER_KEY)
  const error = useReselector(getFetchError, CREATE_QA_USER_KEY)

  const formik = useFormik({
    initialValues: {
      pathwayIdentifier: pathwayIdent || '',
      createEmail: true,
      email: '',
      createNames: true,
      firstName: '',
      lastName: '',
      activeState: 'active' as 'active' | 'onboarding',
      taxEntityType: undefined,
      practiceType: PracticeType.solo,
      membershipStartDate: '',
      filingStatus: FILING_STATUSES.single,
      showOnboardingPage: false,
      stripeProductId: '',
    },
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async ({
      createNames,
      createEmail,
      email: submitEmail,
      membershipStartDate,
      ...rest
    }) => {
      let email

      if (!createEmail) {
        // If we're automatically creating an email address, we want undefined sent to the server
        // Otherwise subaddress the email by adding a UUID
        const parts = submitEmail?.split('@')
        if (parts?.length === 2) {
          email = `${parts[0]}+${faker.string.uuid()}@${parts[1]}`
        }
      }

      const user = await createQAUser({
        email,
        ...rest,
        membershipStartDate: membershipStartDate || undefined,
        ...(createNames ? { firstName: undefined, lastName: undefined } : {}),
      })(dispatch)

      if (user) {
        onClose()
      }
    },
  })

  const { values, submitForm, isValid } = formik
  const fieldNames = getFieldNames(formik)

  const pathwayOptions = useMemo(
    () =>
      pathways.map((pathway, index) => ({
        key: index,
        text: `${pathway.name} (${pathway.adminRoleName || 'Customer'})`,
        value: pathway.identifier,
      })),
    [pathways]
  )

  const selectedPathway = pathways.find(
    (p) => p.identifier === values.pathwayIdentifier
  )

  const subscriptionOptions = useMemo(
    () =>
      stripeProducts
        ?.filter(
          (sub) =>
            sub.active &&
            sub.metadata['heard-product-type'] === 'primary' &&
            sub.metadata.eligibility?.length &&
            (!selectedPathway?.taxEntities?.length ||
              sub.metadata.eligibility.some((el) =>
                selectedPathway?.taxEntities?.includes(el.taxEntityType)
              ))
        )
        .map((sub) => ({
          key: sub.id,
          value: sub.id,
          text: `${sub.name} (ID: ${sub.id})`,
        })) || [],
    [selectedPathway?.taxEntities, stripeProducts]
  )

  const selectedSubscription = useMemo(
    () => stripeProducts?.find((sub) => sub.id === values.stripeProductId),
    [stripeProducts, values.stripeProductId]
  )

  const taxEntityTypeOptions = useMemo(
    () =>
      selectedSubscription
        ? valsToDropdownOptions(
            uniq(
              selectedSubscription.metadata.eligibility?.map(
                (el) => el.taxEntityType
              )
            )
          )
        : TaxEntityTypeOptions,
    [selectedSubscription]
  )

  const practiceTypeOptions = useMemo(
    () =>
      valsToDropdownOptions(
        selectedSubscription
          ? uniq(
              selectedSubscription.metadata.eligibility?.map(
                (el) => el.practiceType
              )
            )
          : ['solo', 'group', 'getting_started']
      ),
    [selectedSubscription]
  )

  // If the selected pathway does not have an admin role specified, assume it is intended for customers
  const isCustomerPathway = isNil(selectedPathway?.adminRoleName)

  return (
    <FormikProvider value={formik}>
      <Dialog open={open} onClose={onClose}>
        <Dialog.Content>
          <div className="flex flex-col gap-5">
            <Dialog.Title>New QA User</Dialog.Title>

            <Form.Select
              label="Choose a pathway"
              required
              name={fieldNames.pathwayIdentifier}
              options={pathwayOptions}
              schema={makeReqStringSchema()}
            />

            <Form.Checkbox
              label="Create random first & last names for me"
              schema={makeReqBoolSchema()}
              name={fieldNames.createNames}
            />

            {!values.createNames && (
              <>
                <Form.Input
                  label="First Name"
                  required
                  shouldClearOnDismount
                  schema={makeReqStringSchema()}
                  name={fieldNames.firstName}
                />
                <Form.Input
                  label="Last Name"
                  required
                  shouldClearOnDismount
                  schema={makeReqStringSchema()}
                  name={fieldNames.lastName}
                />
              </>
            )}
            <Form.Checkbox
              label="Create a random email address for me"
              schema={makeReqBoolSchema()}
              name={fieldNames.createEmail}
            />
            {!formik.values.createEmail && (
              <Form.Input
                label="Email Address"
                required
                shouldClearOnDismount
                schema={makeReqEmailSchema()}
                name={fieldNames.email}
              />
            )}

            <Form.Select
              label="Stripe Product"
              description="If none selected first eligible option will be used.  If non supported selection is made, creation will fail"
              options={subscriptionOptions}
              name={fieldNames.stripeProductId}
            />

            {selectedSubscription && (
              <Card>
                <Text>
                  Info → {selectedSubscription.name} {selectedSubscription.id}
                </Text>
                <Text>
                  Display Name → {selectedSubscription.metadata.displayName}
                </Text>
                <Text>Tier → {selectedSubscription.metadata.tier}</Text>
                <Text>
                  Eligibility →
                  {selectedSubscription.metadata.eligibility?.map((el, idx) => (
                    <span key={idx}>
                      <br />
                      {el.taxEntityType} - {el.practiceType}
                    </span>
                  ))}
                </Text>
              </Card>
            )}

            {isCustomerPathway && (
              <>
                <Form.Select
                  label="User's state"
                  required
                  name={fieldNames.activeState}
                  options={[
                    { text: 'Active', value: 'active' },
                    { text: 'Signup', value: 'onboarding' },
                  ]}
                  schema={makeReqStringSchema()}
                />
                {formik.values.activeState === 'active' && (
                  <Form.Select
                    required
                    label="Show Onboarding?"
                    name={fieldNames.showOnboardingPage}
                    options={YesNoOptions}
                    schema={makeReqStringSchema()}
                  />
                )}

                <Form.Select
                  label="Tax Entity Type (optional)"
                  description="Might be overwritten if this pathway requires a specific tax entity type"
                  name={fieldNames.taxEntityType}
                  options={taxEntityTypeOptions}
                  schema={yup.string().optional()}
                />

                <Form.Select
                  label="Practice Type"
                  description="Might be overwritten if this pathway requires a specific tax entity type"
                  name={fieldNames.practiceType}
                  options={practiceTypeOptions}
                  schema={makeReqStringSchema()}
                />

                <Form.DateInput
                  label="Membership start date"
                  description="If not entered will default to now"
                  name={fieldNames.membershipStartDate}
                />

                <Form.Select
                  name={fieldNames.filingStatus}
                  label="Filing Status"
                  options={FilingStatusOptions}
                />
              </>
            )}
            {error && (
              <Alert title="Something went wrong" description={error.message} />
            )}
            <Dialog.Footer>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                onClick={submitForm}
                disabled={!isValid || loading}
                loading={loading}
              >
                Create
              </Button>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog>
    </FormikProvider>
  )
}

export default NewUserModal
